import { ref, watch, computed, type Ref } from 'vue'
import { apiFetch } from '../services/api'

export interface AccountBasics {
  id: string
  domain: string
  name?: string | null
  linkedin_url?: string | null
  salesforce_account_id?: string | null
  hubspot_company_id?: string | null
  pipedrive_org_id?: string | null
  leadiq?: { enrichment_status?: string | null } | null
}

const LRU_MAX = 1000

/**
 * Module-level LRU cache for account basics.
 * JS Map preserves insertion order — re-inserting on access moves the entry
 * to the end, so the first key is always the oldest (LRU victim).
 */
const _cache = new Map<string, AccountBasics>()

/**
 * Reactive generation counter — bumped on every cache write so watchers
 * that depend on a specific account ID can detect changes.
 */
const _generation = ref(0)

/** In-flight fetch promises keyed by account ID for deduplication. */
const _inflight = new Map<string, Promise<AccountBasics | null>>()

function _lruGet(id: string): AccountBasics | undefined {
  const entry = _cache.get(id)
  if (entry === undefined) return undefined
  // Move to end (most recently used)
  _cache.delete(id)
  _cache.set(id, entry)
  return entry
}

function _lruSet(id: string, data: AccountBasics) {
  _cache.delete(id)
  _cache.set(id, data)
  if (_cache.size > LRU_MAX) {
    const oldest = _cache.keys().next().value
    if (oldest !== undefined) _cache.delete(oldest)
  }
  _generation.value++
}

async function _fetchBasics(accountId: string): Promise<AccountBasics | null> {
  const existing = _inflight.get(accountId)
  if (existing) return existing

  const promise = (async () => {
    try {
      const resp = await apiFetch(`/accounts/${encodeURIComponent(accountId)}/basics`)
      if (resp) {
        _lruSet(accountId, resp as AccountBasics)
        return resp as AccountBasics
      }
      return null
    } catch {
      return null
    } finally {
      _inflight.delete(accountId)
    }
  })()

  _inflight.set(accountId, promise)
  return promise
}

function _ensure(id: string) {
  if (!id) return
  if (_cache.has(id)) return
  if (_inflight.has(id)) return
  void _fetchBasics(id)
}

/**
 * Shared composable for account basics with LRU caching.
 *
 * Multiple callers can use this for different account IDs simultaneously.
 * All callers for the same ID share the same cached data and fetch
 * deduplication. The returned `basics` ref is a computed that reads
 * from the LRU cache reactively.
 */
export function useAccountBasics(accountId: Ref<string> | string) {
  const idRef = typeof accountId === 'string' ? ref(accountId) : accountId

  // Kick off fetch on first call if not cached
  _ensure(idRef.value)

  // Re-fetch when accountId changes
  watch(idRef, (newId) => _ensure(newId))

  const basics = computed<AccountBasics | null>(() => {
    // eslint-disable-next-line no-unused-expressions
    _generation.value // reactive dependency — re-evaluates when any cache write happens
    const id = idRef.value
    return id ? (_lruGet(id) ?? null) : null
  })

  const loading = computed(() => _inflight.has(idRef.value))

  return {
    /** Reactive account basics — null while loading or on error. */
    basics,
    /** True while a fetch is in flight for this account. */
    loading,
    /** Replace the cached basics with fresh data. */
    update(data: AccountBasics) {
      const id = String(data.id || '').trim()
      if (id) _lruSet(id, data)
    },
    /** Force re-fetch from the API, bypassing the LRU cache. */
    refresh() {
      const id = idRef.value
      if (id) void _fetchBasics(id)
    },
  }
}
